import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ObservasjonRow } from './api'

interface Props {
  rows: ObservasjonRow[]
  total: number
  sort: string
  sortDir: 'asc' | 'desc'
  onSort: (key: string) => void
}

const EM_DASH = '—'

const fmtInt = (v: number | null | undefined) =>
  v === null || v === undefined ? EM_DASH : v.toLocaleString('nb-NO')

const fmtKr = (v: number | null | undefined) =>
  v === null || v === undefined ? EM_DASH : Math.round(v).toLocaleString('nb-NO')

const fmtDate = (s: string | null | undefined) => {
  if (!s) return EM_DASH
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleDateString('nb-NO', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

const SORTABLE_KEYS = new Set([
  'merke',
  'modell',
  'aarsmodell',
  'belop',
  'dato_overforing',
  'forstegang_reg_norge_dato',
  'klasse',
  'seller_kommunenavn',
  'seller_fylke',
  'buyer_kommunenavn',
  'buyer_fylke',
])

const COLUMNS = [
  { key: 'merke', labelKey: 'priser.cols.merke', align: 'left' },
  { key: 'modell', labelKey: 'priser.cols.modell', align: 'left' },
  { key: 'aarsmodell', labelKey: 'priser.cols.aarsmodell', align: 'right' },
  { key: 'belop', labelKey: 'priser.cols.belop', align: 'right' },
  { key: 'dato_overforing', labelKey: 'priser.cols.datoOverdragelse', align: 'left' },
  { key: 'forstegang_reg_norge_dato', labelKey: 'priser.cols.forstegangsReg', align: 'left' },
  { key: 'klasse', labelKey: 'priser.cols.klasse', align: 'left' },
  { key: 'seller_kommunenavn', labelKey: 'priser.cols.selgerKommune', align: 'left' },
  { key: 'seller_fylke', labelKey: 'priser.cols.selgerFylke', align: 'left' },
  { key: 'buyer_kommunenavn', labelKey: 'priser.cols.kjoperKommune', align: 'left' },
  { key: 'buyer_fylke', labelKey: 'priser.cols.kjoperFylke', align: 'left' },
] as const

export default function ObservasjonerGrid({ rows, total, sort, sortDir, onSort }: Props) {
  const { t } = useTranslation('bruktbil')
  function sortIcon(key: string) {
    if (!SORTABLE_KEYS.has(key)) return null
    if (sort !== key) return <ChevronsUpDown className="w-3.5 h-3.5 inline ml-1 opacity-40" />
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 inline ml-1" />
      : <ArrowDown className="w-3.5 h-3.5 inline ml-1" />
  }

  if (rows.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-gray-400 dark:text-gray-500">
        {t('priser.ingenObservasjoner')}
      </div>
    )
  }

  return (
    <div className="card p-6" data-testid="observasjoner-grid">
      <div className="overflow-x-auto">
        <table className="data-table data-table-compact w-full text-sm">
          <thead>
            <tr>
              {COLUMNS.map(c => (
                <th
                  key={c.key}
                  onClick={() => SORTABLE_KEYS.has(c.key) && onSort(c.key)}
                  className={`${SORTABLE_KEYS.has(c.key) ? 'cursor-pointer select-none' : ''} ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {t(c.labelKey)}{sortIcon(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
              <td colSpan={3} className="text-left">{t('priser.observasjonerTotalt', { n: fmtInt(total) })}</td>
              <td colSpan={COLUMNS.length - 3} className="text-right text-gray-400 dark:text-gray-500 pr-3">
                {t('priser.viserPaSiden', { n: rows.length })}
              </td>
            </tr>
            {rows.map((r, idx) => (
              <tr
                key={r.svv_id}
                className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${
                  idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''
                }`}
              >
                <td className="text-left">{r.merke ?? EM_DASH}</td>
                <td className="text-left">{r.modell ?? EM_DASH}</td>
                <td className="text-right tabular-nums">{r.aarsmodell ?? EM_DASH}</td>
                <td className="text-right tabular-nums font-medium">{fmtKr(r.belop)}</td>
                <td className="text-left">{fmtDate(r.dato_overforing)}</td>
                <td className="text-left">{fmtDate(r.forstegang_reg_norge_dato)}</td>
                <td className="font-mono text-xs text-primary-600 dark:text-primary-400">
                  {r.klasse ?? EM_DASH}
                </td>
                <td className="text-left">{r.seller_kommunenavn ?? EM_DASH}</td>
                <td className="text-left">{r.seller_fylke ?? EM_DASH}</td>
                <td className="text-left">{r.buyer_kommunenavn ?? EM_DASH}</td>
                <td className="text-left">{r.buyer_fylke ?? EM_DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
